// src/components/AddPhishingURLModal.js
import React, { useState } from 'react';
import { X, Shield, AlertCircle } from 'lucide-react';

const PHISHING_TYPES = [
  'Credential Harvesting',
  'Malware Distribution',
  'Business Email Compromise',
  'Invoice Fraud',
  'Tech Support Scam',
  'Fake Login Page',
  'Data Theft',
  'Cryptocurrency Scam',
  'Other'
];

const RISK_LEVELS = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
const STATUS_OPTIONS = ['Active', 'Monitoring', 'Takedown Requested', 'Taken Down', 'Resolved'];

const SECTORS = [
  'Financial',
  'Healthcare',
  'Technology',
  'Government',
  'Education',
  'Retail',
  'Manufacturing',
  'Energy',
  'Telecommunications',
  'Other'
];

const AddPhishingURLModal = ({ isOpen, onClose, onURLAdded }) => {
  const [formData, setFormData] = useState({
    url: '',
    target_sector: '',
    target_country: '',
    targeted_brand: '',
    phishing_type: 'Credential Harvesting',
    risk_level: 'Medium',
    status: 'Active',
    first_seen_date: new Date().toISOString().split('T')[0],
    ip_address: '',
    hosting_provider: '',
    registrar: '',
    ssl_certificate: false,
    detection_method: '',
    source: '',
    threat_actor: '',
    campaign_id: '',
    page_title: '',
    screenshot_url: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setFormData({
      url: '',
      target_sector: '',
      target_country: '',
      targeted_brand: '',
      phishing_type: 'Credential Harvesting',
      risk_level: 'Medium',
      status: 'Active',
      first_seen_date: new Date().toISOString().split('T')[0],
      ip_address: '',
      hosting_provider: '',
      registrar: '',
      ssl_certificate: false,
      detection_method: '',
      source: '',
      threat_actor: '',
      campaign_id: '',
      page_title: '',
      screenshot_url: '',
      notes: ''
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await window.Clerk?.session?.getToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      if (!formData.url) {
        throw new Error('URL is required');
      }

      const response = await fetch('/api/phishing-feed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          url: formData.url,
          target_sector: formData.target_sector || null,
          target_country: formData.target_country || null,
          targeted_brand: formData.targeted_brand || null,
          phishing_type: formData.phishing_type || null,
          risk_level: formData.risk_level,
          status: formData.status,
          first_seen_date: formData.first_seen_date || null,
          ip_address: formData.ip_address || null,
          hosting_provider: formData.hosting_provider || null,
          registrar: formData.registrar || null,
          ssl_certificate: formData.ssl_certificate,
          detection_method: formData.detection_method || null,
          source: formData.source || null,
          threat_actor: formData.threat_actor || null,
          campaign_id: formData.campaign_id || null,
          page_title: formData.page_title || null,
          screenshot_url: formData.screenshot_url || null,
          notes: formData.notes || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add phishing URL');
      }

      console.log('Phishing URL added:', data);
      
      if (onURLAdded) {
        onURLAdded(data);
      }

      resetForm();
      onClose();
      
      alert(`✅ Phishing URL added successfully! (ID: ${data.feed_id})`);

    } catch (err) {
      console.error('Error adding phishing URL:', err);
      setError(err.message || 'Failed to add phishing URL');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-400" />
                Add Phishing URL
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Add a new phishing URL to the global threat feed
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
              disabled={loading}
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* URL Information */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Phishing URL
              </h3>
              
              <div className="space-y-4">
                
                {/* URL */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Phishing URL <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono text-sm"
                    placeholder="https://evil-paypal.com/login"
                    required
                    disabled={loading}
                  />
                </div>

              </div>
            </div>

            {/* Classification */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Classification
              </h3>
              
              <div className="space-y-4">
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Targeted Brand
                    </label>
                    <input
                      type="text"
                      value={formData.targeted_brand}
                      onChange={(e) => setFormData({ ...formData, targeted_brand: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., PayPal, Microsoft"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Phishing Type
                    </label>
                    <select
                      value={formData.phishing_type}
                      onChange={(e) => setFormData({ ...formData, phishing_type: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {PHISHING_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Risk Level
                    </label>
                    <select
                      value={formData.risk_level}
                      onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {RISK_LEVELS.map(risk => (
                        <option key={risk} value={risk}>{risk}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      First Seen
                    </label>
                    <input
                      type="date"
                      value={formData.first_seen_date}
                      onChange={(e) => setFormData({ ...formData, first_seen_date: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Target Sector
                    </label>
                    <select
                      value={formData.target_sector}
                      onChange={(e) => setFormData({ ...formData, target_sector: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      disabled={loading}
                    >
                      <option value="">Select sector (optional)</option>
                      {SECTORS.map(sector => (
                        <option key={sector} value={sector}>{sector}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Target Country
                    </label>
                    <input
                      type="text"
                      value={formData.target_country}
                      onChange={(e) => setFormData({ ...formData, target_country: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., USA, Global"
                      disabled={loading}
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* Technical Details */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Technical Details (Optional)
              </h3>
              
              <div className="space-y-4">
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      IP Address
                    </label>
                    <input
                      type="text"
                      value={formData.ip_address}
                      onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono"
                      placeholder="192.168.1.1"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Hosting Provider
                    </label>
                    <input
                      type="text"
                      value={formData.hosting_provider}
                      onChange={(e) => setFormData({ ...formData, hosting_provider: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Cloudflare"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Detection Method
                    </label>
                    <input
                      type="text"
                      value={formData.detection_method}
                      onChange={(e) => setFormData({ ...formData, detection_method: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., Manual Report, Automated Scan"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Source
                    </label>
                    <input
                      type="text"
                      value={formData.source}
                      onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      placeholder="e.g., PhishTank, User Report"
                      disabled={loading}
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* Notes */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Additional Information
              </h3>
              
              <div className="space-y-4">
                
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={4}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="Additional details about this phishing URL..."
                    disabled={loading}
                  />
                </div>

              </div>
            </div>

          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.url}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Adding...
              </>
            ) : (
              'Add Phishing URL'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddPhishingURLModal;